import React, { JSX, ReactNode, useCallback, useEffect, useRef } from 'react';
import { useWebSocket } from 'src/hooks/useWebSocket';
import type {
  WSProcessingStarted,
  WSStepStarted,
  WSStepCompleted,
  WSStepFailed,
  WSNeedsClassification,
  WSNeedsMemberConfirmation,
  WSProcessingCompleted,
  WSProcessingFailed,
  WSFileEscalated,
  WSDocumentsSplit,
  RenterFileListItem,
} from 'src/types/aiFileUploadV2';

const WS_PATH = 'renter_hub/user/';

interface Props {
  children: ReactNode;
  enabled?: boolean;
  onPatch: (
    id: number,
    patch: Partial<RenterFileListItem>,
    seed?: Partial<RenterFileListItem>
  ) => void;
  onStepCompleted: (id: number, stepNumber: number) => void;
  onRefetch: () => void;
  /** Additional message handlers merged on top of the built-in file-processing ones. */
  extraMessageHandlers?: Record<string, (data: unknown) => void>;
}

export function AIFileUploadV2WebSocketProvider({
  children,
  enabled = true,
  onPatch,
  onStepCompleted,
  onRefetch,
  extraMessageHandlers = {},
}: Props): JSX.Element {
  const sendMessageRef = useRef<((msg: unknown) => void) | null>(null);
  const hasConnectedOnceRef = useRef<boolean>(false);
  // Track last step_number per file so step_completed can set completed_steps accurately
  const stepNumberRef = useRef<Record<number, number>>({});

  const handleProcessingStarted = useCallback(
    (data: unknown): void => {
      const e = data as WSProcessingStarted;
      onPatch(
        e.file_id,
        {
          status: 'processing',
          total_steps: e.total_steps,
          completed_steps: 0,
          target_qit_id: e.target_qit_id ?? null,
        },
        // Seed with file metadata so the row renders immediately without a fetch
        {
          uuid: e.uuid,
          original_filename: e.original_filename,
          mime_type: e.mime_type,
          file_size_bytes: e.file_size_bytes,
          status: 'processing',
          total_steps: e.total_steps,
          completed_steps: 0,
          target_qit_id: e.target_qit_id ?? null,
          // Sensible defaults for fields required by RenterFileListItem
          session_status: 'active',
          needs_manual_classification: false,
          is_duplicate: false,
          is_valid: null,
          match_count: 0,
        }
      );
    },
    [onPatch]
  );

  const handleStepStarted = useCallback(
    (data: unknown): void => {
      const e = data as WSStepStarted;
      stepNumberRef.current[e.file_id] = e.step_number;
      onPatch(e.file_id, {
        status: 'processing',
        // Push the snake_case step name (e.g. 'ocr_extraction'), NOT the
        // human display_name. The frontend maps this through getStepLabel
        // to user-facing copy ("Reading text"), and the backend's
        // RenterFileListItem.current_step is itself the snake_case name —
        // so this keeps WS-driven state consistent with REST-driven state.
        current_step: e.step_name,
        total_steps: e.total_steps,
      });
    },
    [onPatch]
  );

  const handleStepCompleted = useCallback(
    (data: unknown): void => {
      const e = data as WSStepCompleted;
      const stepNumber = stepNumberRef.current[e.file_id];
      if (stepNumber !== undefined) {
        onStepCompleted(e.file_id, stepNumber);
      }
      // A step can complete with passed=false and is_blocking=true — treat as failure
      if (e.data?.passed === false && e.data?.is_blocking === true) {
        onPatch(
          e.file_id,
          {
            status: 'failed',
            error_message:
              e.data.error_message ??
              e.data.message ??
              `Step failed: ${e.step_name}`,
            error_step: e.step_name,
            error_detail: e.data.error_detail ?? null,
          },
          undefined,
          true
        );
      }
    },
    [onPatch, onStepCompleted]
  );

  const handleStepFailed = useCallback(
    (data: unknown): void => {
      const e = data as WSStepFailed;
      if (e.is_blocking) {
        onPatch(
          e.file_id,
          {
            status: 'failed',
            error_message: e.error_message ?? e.error,
            error_step: e.step_name,
            error_detail: e.error_detail ?? null,
          },
          undefined,
          true
        );
      }
    },
    [onPatch]
  );

  const handleNeedsClassification = useCallback(
    (data: unknown): void => {
      const e = data as WSNeedsClassification;
      onPatch(e.file_id, { status: 'needs_classification' });
    },
    [onPatch]
  );

  // Renter-interview cross-member mismatch pause (verify mode) — sibling of
  // handleNeedsClassification above, same simple status-patch shape. Unlike
  // needs_classification (whose resolution UI, ClassifyWidget, re-fetches
  // its own data from the doc-schema endpoints), the three-way confirm/
  // reject/delete widget needs the candidate/current member identity to
  // render — carried directly on the WS payload, so we patch it onto
  // member_confirmation alongside status rather than requiring a second
  // round-trip. See docs/plans/sorting-hat-cross-member-and-unification.md §1.
  const handleNeedsMemberConfirmation = useCallback(
    (data: unknown): void => {
      const e = data as WSNeedsMemberConfirmation;
      onPatch(e.file_id, {
        status: 'needs_member_confirmation',
        member_confirmation: {
          candidate_cert_member_id: e.candidate_cert_member_id,
          candidate_full_name: e.candidate_full_name,
          current_cert_member_id: e.current_cert_member_id,
          current_full_name: e.current_full_name,
        },
      });
    },
    [onPatch]
  );

  const handleProcessingCompleted = useCallback(
    (data: unknown): void => {
      const e = data as WSProcessingCompleted;
      onPatch(e.file_id, { status: 'completed' }, {
        status: 'completed',
      } as Partial<RenterFileListItem>);
    },
    [onPatch]
  );

  const handleProcessingFailed = useCallback(
    (data: unknown): void => {
      const e = data as WSProcessingFailed;
      // deleteFromList so the failure lands in failedEntries and the user
      // gets an error banner even for unexpected pipeline failures.
      onPatch(
        e.file_id,
        {
          status: 'failed',
          error_message: e.error,
          error_step: e.failed_step,
        },
        undefined,
        true
      );
      onRefetch();
    },
    [onPatch, onRefetch]
  );

  const handleFileEscalated = useCallback(
    (data: unknown): void => {
      const e = data as WSFileEscalated;
      onPatch(e.file_id, { user_escalated: true });
    },
    [onPatch]
  );

  // Multi-document split: the parent upload was separated into child documents.
  // Mark the parent done ("Split into N documents") and seed each child as
  // queued with a "Split from <parent>" provenance label, so the rows render
  // immediately — the children's own processing_started/step events then move
  // them through Processing → Accepted.
  const handleDocumentsSplit = useCallback(
    (data: unknown): void => {
      const e = data as WSDocumentsSplit;
      onPatch(e.file_id, {
        status: 'completed',
        session_status: 'superseded',
        split_into: e.child_count,
      });
      e.children.forEach((child) => {
        onPatch(
          child.id,
          {
            status: 'pending',
            split_from_filename: e.parent_filename ?? null,
            doc_sub_type_key: child.doc_sub_type_key,
          },
          {
            uuid: child.uuid,
            original_filename: child.original_filename,
            mime_type: child.mime_type,
            file_size_bytes: child.file_size_bytes,
            status: 'pending',
            total_steps: 0,
            completed_steps: 0,
            target_qit_id: e.target_qit_id ?? null,
            session_status: 'active',
            needs_manual_classification: false,
            is_duplicate: false,
            is_valid: null,
            match_count: 0,
            split_from_filename: e.parent_filename ?? null,
          }
        );
      });
    },
    [onPatch]
  );

  const handleConnect = useCallback((): void => {
    if (!hasConnectedOnceRef.current) {
      hasConnectedOnceRef.current = true;
    }
    // Always refetch on connect — on first mount the WS may have missed
    // processing events that the Celery task sent before the connection
    // was fully established (especially in dev where tasks run instantly).
    onRefetch();
  }, [onRefetch]);

  const { sendMessage } = useWebSocket({
    enabled,
    url: WS_PATH,
    messageHandlers: {
      'renter_hub.processing_started': handleProcessingStarted,
      'renter_hub.step_started': handleStepStarted,
      'renter_hub.step_completed': handleStepCompleted,
      'renter_hub.step_failed': handleStepFailed,
      'renter_hub.needs_classification': handleNeedsClassification,
      'renter_hub.needs_member_confirmation': handleNeedsMemberConfirmation,
      'renter_hub.processing_completed': handleProcessingCompleted,
      'renter_hub.processing_failed': handleProcessingFailed,
      'renter_hub.file_escalated': handleFileEscalated,
      'renter_hub.documents_split': handleDocumentsSplit,
      ...extraMessageHandlers,
    },
    onConnect: handleConnect,
  });

  useEffect(() => {
    sendMessageRef.current = sendMessage;
  }, [sendMessage]);

  return (
    <>
      {/* T5-14: aria-live region for coarse processing status */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        id="renter-hub-ws-status"
      />
      {children}
    </>
  );
}
